/**
 * The unread notification banner stack.
 *
 * Renders EVERY unread, unexpired notification as a compact full-width strip
 * above the page — the same shape as TrustScoreWarningBanner, so it stacks
 * cleanly with the existing top-of-page strips.
 *
 * Mounted as a sibling above the page wrapper in App.tsx (not inside the page)
 * so the strips run edge to edge.
 *
 * Renders nothing when there is nothing unread, when the provider is absent,
 * or when the fetch failed.
 */

import { useState } from 'react';
import { X } from 'lucide-react';
import { useNotifications, type ActiveNotification } from '../hooks/useNotifications';
import { severityStyle } from './notificationStyles';
import NotificationDetailModal from './NotificationDetailModal';

/** At or above this many banners, offer a single "dismiss all" action. */
const DISMISS_ALL_THRESHOLD = 3;

export default function NotificationBanners() {
  const { active, markRead, markAllRead } = useNotifications();
  const [selected, setSelected] = useState<ActiveNotification | null>(null);

  if (active.length === 0) return null;

  return (
    <>
      {active.map((n) => {
        const style = severityStyle(n.severity);
        const Icon = style.Icon;

        return (
          /* The ENTIRE strip is the hit area for opening the full message —
             padding and dead space included, not just the text.

             The CTA link and the dismiss X live inside that area, so both
             stopPropagation on click: they do their own job without also
             opening the modal. Nothing else in here is clickable — the body
             is plain text, so a URL typed into it is not a link.

             This is a role="button" div rather than a real <button> because it
             must contain an <a> and a <button>, neither of which may be nested
             inside a <button> element. Keyboard parity is restored with
             tabIndex + the Enter/Space handler below. */
          <div
            key={n.id}
            role="button"
            tabIndex={0}
            title="View full message"
            onClick={() => setSelected(n)}
            onKeyDown={(e) => {
              // keydown bubbles, so without this guard pressing Enter on the
              // focused CTA link would follow the link AND open the modal.
              // Only act when the strip itself holds focus.
              if (e.target !== e.currentTarget) return;
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setSelected(n);
              }
            }}
            className={`group w-full text-sm py-2.5 px-4 cursor-pointer ${style.strip}`}
          >
            <div className="max-w-7xl mx-auto flex items-start justify-between gap-3">
              <div className="flex items-start gap-2 min-w-0 flex-1 text-left">
                <Icon className={`w-4 h-4 flex-shrink-0 mt-0.5 ${style.icon}`} />
                <div className="min-w-0">
                  <div className="font-medium truncate group-hover:underline">
                    {n.title}
                  </div>
                  {/* Two-line teaser. Deliberately NOT whitespace-pre-line:
                      newlines collapse to spaces so the preview reads as one
                      flowing sentence instead of burning both lines on a
                      paragraph break. The full body, with line breaks intact,
                      is one click away in the detail modal. */}
                  {n.body && (
                    <div className="mt-0.5 text-xs opacity-80 line-clamp-2 break-words">
                      {n.body}
                    </div>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-2 flex-shrink-0">
                {n.link_url && (
                  <a
                    href={n.link_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className={`px-3 py-1 text-xs font-semibold rounded transition-colors ${style.button}`}
                  >
                    {n.link_label || 'Learn more'}
                  </a>
                )}
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    void markRead(n.id);
                  }}
                  className={`p-1 transition-colors ${style.dismiss}`}
                  title="Dismiss"
                  aria-label={`Dismiss notification: ${n.title}`}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        );
      })}

      {active.length >= DISMISS_ALL_THRESHOLD && (
        <div className="w-full bg-gray-50 border-b border-gray-200 py-1.5 px-4">
          <div className="max-w-7xl mx-auto flex justify-end">
            <button
              type="button"
              onClick={() => void markAllRead()}
              className="text-xs font-medium text-gray-600 hover:text-gray-900 transition-colors"
            >
              Dismiss all ({active.length})
            </button>
          </div>
        </div>
      )}

      <NotificationDetailModal
        notification={selected}
        isOpen={selected !== null}
        onClose={() => setSelected(null)}
        onMarkRead={(id) => void markRead(id)}
      />
    </>
  );
}
